/**
 * HydraCompositeNode - Base for two-input Hydra composites
 *
 * Extends HydraBaseNode for renderToCanvas functionality.
 * Takes two hydra inputs, combines them with a composite/modulate function.
 * Used for blend, add, mult, modulate, etc.
 */

import { HydraBaseNode } from './HydraBaseNode';
import type { HydraFunctionMeta } from '../HydraEndpoint';
import { Dependencies } from '@/nodes/Dependencies';

export interface HydraCompositeConfig {
  /** The Hydra function name (e.g., 'add', 'blend', 'modulate') */
  hydraFn: string;
  /** Endpoint names in order they're passed to the Hydra function (after the second input) */
  fnArgs: string[];
}

/**
 * Base class for two-input Hydra composite nodes.
 * Receives two input chains, outputs combined chain with composite metadata.
 */
export class HydraCompositeNode<T extends Record<string, any>> extends HydraBaseNode<T> {
  protected hydraFn: string = '';
  protected fnArgs: string[] = [];

  constructor(id: string, deps: Dependencies, config: HydraCompositeConfig) {
    super(id, deps);
    this.hydraFn = config.hydraFn;
    this.fnArgs = config.fnArgs;
  }

  /** Return this node's metadata for graph-query chain building.
   *  The secondary chain arg is NOT included here — buildChainFromGraph
   *  builds it from the graph and inserts it as the first arg. */
  getHydraMeta(): HydraFunctionMeta {
    return { composite: true, name: this.hydraFn, args: this.createArgGetters(this.fnArgs) };
  }

  // Chain is now built by HydraRenderer via graph query — no propagation needed
}
